import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import cidadesData from '../../Utils/cidades.json';
import "./MoveisManagementPage.css";

// Modal Component for Create and Edit Forms
const Modal = ({ isOpen, onClose, children }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          X
        </button>
        {children}
      </div>
    </div>
  );
};

const districts = Object.keys(cidadesData);

const MoveisManagementPage = () => {
  const [moveis, setMoveis] = useState([]);
  const [newMovel, setNewMovel] = useState({
    active: true,
    premium: false,
    opportunity: false,
    title: "",
    description: "",
    image: null,
    price: "",
    district: "",
    city: "", // <-- Add city
    images_path: [],
    user_id: localStorage.getItem("currentUserId"),
    prompt_image: "",
  });
  const [editMovel, setEditMovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [allUsers, setAllUsers] = useState([]);
  const userRole = localStorage.getItem("userRole");
  const user_id = localStorage.getItem("currentUserId");
  const [district, setDistrict] = useState('');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState([]);
  // Remove: const districts = React.useMemo(() => Array.from(new Set(cidadesData.map(c => c.distrito))), []);

  const getApiUrl = () => {
    return "/api"; // Standardized API base path
  };

  const API_URL = getApiUrl(); // Use standardized API URL

  useEffect(() => {
    fetchMoveis();
  }, []);

  // Fetch all users for admin owner change
  useEffect(() => {
    if (userRole === "Admin") {
      axios.get(`${API_URL}/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then(res => setAllUsers(res.data))
      .catch(() => setAllUsers([]));
    }
  }, [userRole]);

  // Update cities when district changes
  useEffect(() => {
    if (district) {
      setCities(cidadesData[district] || []);
    } else {
      setCities([]);
    }
    setCity('');
    if (editMovel) {
      setEditMovel(movel => movel ? { ...movel, district: district, city: '' } : movel);
    } else {
      setNewMovel(nm => ({ ...nm, district: district, city: '' }));
    }
    // eslint-disable-next-line
  }, [district]);

  useEffect(() => {
    if (editMovel) {
      setEditMovel(movel => movel ? { ...movel, city: city } : movel);
    } else {
      setNewMovel(nm => ({ ...nm, city: city }));
    }
    // eslint-disable-next-line
  }, [city]);

  const fetchMoveis = () => {
    setLoading(true);
    const token = localStorage.getItem("authToken");

    axios
      .get(`${API_URL}/users/moveis`, { // <-- correct: /users/moveis
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        // Ensure images_path is always an array for each movel
        const moveisWithImages = response.data.map(movel => {
          let images_path = movel.images_path;
          if (typeof images_path === "string") {
            try {
              images_path = JSON.parse(images_path);
            } catch {
              images_path = [];
            }
          }
          return {
            ...movel,
            images_path: Array.isArray(images_path) ? images_path : [],
            prompt_image: movel.prompt_image || (Array.isArray(images_path) && images_path[0]) || "",
          };
        });
        setMoveis(moveisWithImages);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching moveis:", error);
        setLoading(false);
      });
  };

  // Utility to filter only allowed fields for backend
  const filterMovelPayload = (movel) => {
    return {
      id: movel.id,
      title: movel.title,
      district: movel.district,
      price: movel.price,
      description: movel.description,
      active: movel.active,       
      premium: movel.premium,      
      opportunity: movel.opportunity, 
      user_id: movel.user_id,
      images_path: movel.images_path, // <-- send as array, not JSON string
      prompt_image: movel.prompt_image,
    };
  };

  // Always update selectedFiles on file input change
  const handleImageUploadInput = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  // Upload images for a given movelId, always use selectedFiles
  const handleImageUpload = async (movelId) => {
    const formData = new FormData();
    const token = localStorage.getItem("authToken");
    if (selectedFiles.length > 0) {
      // Use images[] for multiple files
      selectedFiles.forEach(file => {
        formData.append("images[]", file);
      });
      formData.append("movelId", movelId);

      try {
        const res = await axios.post(
          `${API_URL}/users/moveis/upload`, // <-- correct: /users/moveis/upload
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        setSelectedFiles([]); // Clear after upload
        return res.data;
      } catch (error) {
        console.error("Error uploading images:", error);
      }
    }
  };

  // On create, always use selectedFiles for upload, then clear input and selectedFiles
  const handleCreateMovel = async () => {
    if (!validateInputs(newMovel)) return;

    const token = localStorage.getItem("authToken");

    const data = {
      ...newMovel,
      images_path: [],
      prompt_image: "",
    };

    try {
      const response = await axios.post(`${API_URL}/users/moveis`, data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      });

      const createdMovel = response.data;

      let uploadedFileNames = [];
      if (selectedFiles.length > 0) {
        const uploadResult = await handleImageUpload(createdMovel.id);
        if (uploadResult && Array.isArray(uploadResult.uploaded)) {
          uploadedFileNames = uploadResult.uploaded;
        }
      }

      if (uploadedFileNames.length > 0) {
        await axios.put(
          `${API_URL}/users/moveis/${createdMovel.id}`, // <-- correct: /users/moveis/{id}
          {
            ...newMovel,
            id: createdMovel.id,
            images_path: uploadedFileNames,
            prompt_image: uploadedFileNames[0],
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      fetchMoveis();

      setNewMovel({
        active: true,
        premium: false,
        opportunity: false,
        title: "",
        description: "",
        image: null,
        price: "",
        district: "",
        city: "",
        images_path: [],
        user_id: localStorage.getItem("currentUserId"),
        prompt_image: "",
      });
      setIsModalOpen(false);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";

    } catch (error) {
      console.error("Error creating movel:", error);
    }
  };

  // On edit, always use selectedFiles for upload, then clear input and selectedFiles
  const handleUpdateMovel = async () => {
    const userRole = localStorage.getItem("userRole");
    const currentUserId = localStorage.getItem("currentUserId");
    const token = localStorage.getItem("authToken");

    if (userRole !== "Admin" && editMovel.user_id != currentUserId) {
      alert("Não tem permissões para editar este Imóvel.");
      return;
    }

    if (!validateInputs(editMovel)) return;

    let uploadedFileNames = [];
    if (selectedFiles.length > 0) {
      const uploadResult = await handleImageUpload(editMovel.id);
      if (uploadResult && Array.isArray(uploadResult.uploaded)) {
        uploadedFileNames = uploadResult.uploaded;
      }
    }

    const mergedImagesPath = [
      ...(editMovel.images_path || []),
      ...uploadedFileNames.filter(f => !(editMovel.images_path || []).includes(f))
    ];

    const data = {
      ...editMovel,
      images_path: mergedImagesPath,
      prompt_image: editMovel.prompt_image || mergedImagesPath[0] || "",
      id: editMovel.id,
    };

    try {
      await axios.put(
        `${API_URL}/users/moveis/${editMovel.id}`, // <-- correct: /users/moveis/{id}
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchMoveis();

      setEditMovel(null);
      setIsModalOpen(false);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Error updating movel:", error);
    }
  };

  const handleToggleActive = async (movel) => {
    const token = localStorage.getItem("authToken");
    try {
      await axios.put(
        `${API_URL}/users/moveis/${movel.id}/toggle-active`, // <-- correct: /users/moveis/{id}/toggle-active
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchMoveis();
    } catch (error) {
      console.error("Error toggling movel status:", error);
    }
  };

  // When opening modal for edit, set district/city state and cities list
  const handleEditMovel = (movel) => {
    let images_path = movel.images_path;
    if (typeof images_path === "string") {
      try {
        images_path = JSON.parse(images_path);
      } catch {
        images_path = [];
      }
    }
    const promptImage = movel.prompt_image || (Array.isArray(images_path) && images_path[0]) || "";
    setEditMovel({
      ...movel,
      images_path: Array.isArray(images_path) ? images_path : [],
      prompt_image: promptImage,
    });
    setDistrict(movel.district || '');
    setCities(movel.district ? (cidadesData[movel.district] || []) : []);
    setCity(movel.city || '');
    if (fileInputRef.current) fileInputRef.current.value = "";
    setSelectedFiles([]);
    setIsModalOpen(true);
  };

  // Remove an image from the images_path array for the movel being edited
  const handleDeleteImage = (index) => {
    if (!editMovel) return;
    const updatedImages = [...editMovel.images_path];
    const removedImage = updatedImages.splice(index, 1)[0];
    let newPromptImage = editMovel.prompt_image;
    // If the removed image was the prompt_image, pick another or empty
    if (removedImage === editMovel.prompt_image) {
      newPromptImage = updatedImages[0] || "";
    }
    setEditMovel({
      ...editMovel,
      images_path: updatedImages,
      prompt_image: newPromptImage,
    });
  };

  // Set the prompt_image for the movel being edited
  const handleSetPromptImage = (image) => {
    if (!editMovel) return;
    setEditMovel({
      ...editMovel,
      prompt_image: image,
    });
  };

  // Delete a movel by id (with permission check)
  const handleDeleteMovel = async (id, movelUserId) => {
    const userRole = localStorage.getItem("userRole");
    const currentUserId = localStorage.getItem("currentUserId");
    const token = localStorage.getItem("authToken");

    if (userRole !== "Admin" && movelUserId != currentUserId) {
      alert("Não tem permissões para apagar este Imóvel.");
      return;
    }

    if (!window.confirm("Tem a certeza que deseja apagar este imóvel?")) return;

    try {
      await axios.delete(`${API_URL}/users/moveis/${id}`, { // <-- correct: /users/moveis/{id}
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchMoveis();
    } catch (error) {
      console.error("Erro ao apagar imóvel:", error);
      alert("Erro ao apagar imóvel.");
    }
  };

  const validateInputs = (movel) => {
    if (!movel.title || !movel.district || !movel.price  || !movel.description) {
      alert("Todos os campos são necessários.");
      return false;
    }

    if (isNaN(movel.price) || movel.price <= 0) {
      alert("Preço tem de ser um valor positivo.");
      return false;
    }

    return true;
  };

  return (
    <div className="moveis-management">
      <h1>Gestão dos Outros</h1>
      <div className="movel-form">
        <button
          className="create-movel-button"
          onClick={() => {
            setNewMovel({
              active: true,
              premium: false,
              opportunity: false,
              title: "",
              description: "",
              image: null,
              price: "",
              district: "",
              city: "",
              images_path: [],
              user_id: localStorage.getItem("currentUserId"),
              prompt_image: "",
            });
            setEditMovel(null);
            setDistrict('');
            setCity('');
            setCities([]);
            setIsModalOpen(true);
          }}
        >
          Criar Outro
        </button>
      </div>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <h2>{editMovel ? "Editar Outro" : "Criar Outro"}</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <div style={{ flex: "1 1 250px", minWidth: 200 }}>
            <input
              type="text"
              placeholder="Título"
              value={editMovel ? editMovel.title : newMovel.title}
              onChange={e => {
                const value = e.target.value;
                editMovel
                  ? setEditMovel({ ...editMovel, title: value })
                  : setNewMovel({ ...newMovel, title: value });
              }}
            />
          </div>
          <div style={{ flex: "1 1 180px", minWidth: 150 }}>
            <select
              value={district}
              onChange={e => setDistrict(e.target.value)}
            >
              <option value="">Selecionar Distrito</option>
              {districts.map((d, idx) => (
                <option key={idx} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 180px", minWidth: 150 }}>
            <select
              value={city}
              onChange={e => setCity(e.target.value)}
              disabled={!district}
            >
              <option value="">Selecionar Cidade</option>
              {cities.map((c, idx) => (
                <option key={idx} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 120px", minWidth: 100 }}>
            <input
              type="text"
              placeholder="Preço"
              value={editMovel ? editMovel.price : newMovel.price}
              onChange={e => {
                const value = e.target.value;
                editMovel
                  ? setEditMovel({ ...editMovel, price: value })
                  : setNewMovel({ ...newMovel, price: value });
              }}
            />
          </div>
        </div>
        <input
          type="file"
          multiple
          onChange={handleImageUploadInput}
          aria-label="Selecionar fotografias"
          ref={fileInputRef}
          style={{ marginTop: 10 }}
        />
        
        {editMovel && Array.isArray(editMovel.images_path) && editMovel.images_path.length > 0 ? (
          <div className="existing-images">
            <h3>Imagens existentes</h3>
            <table>
              <thead>
                <tr>
                  <th>Imagens</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {editMovel.images_path.map((image, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        <img
                          src={`/Photos/Moveis/${editMovel.id}/${image}`} 
                          alt={`Preview ${image}`} 
                          style={{ width: "100px", height: "auto" }}
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </td>
                      <td>
                        <button onClick={() => handleDeleteImage(index)}>
                          Apagar
                        </button>
                        <button
                          style={{
                            marginLeft: 8,
                            background: editMovel.prompt_image === image ? "#FFD700" : undefined,
                            color: editMovel.prompt_image === image ? "#333" : undefined,
                          }}
                          onClick={() => handleSetPromptImage(image)}
                        >
                          Definir como imagem principal
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: 8 }}>
              <strong>prompt_image:</strong> {editMovel.prompt_image}
            </div>
          </div>
        ) : (
          <p>Sem imagens disponíveis</p>
        )}
        <br />
        <textarea
          placeholder="Descrição"
          value={editMovel ? editMovel.description : newMovel.description}
          onChange={e => {
            const value = e.target.value;
            editMovel
              ? setEditMovel({ ...editMovel, description: value })
              : setNewMovel({ ...newMovel, description: value });
          }}
          rows={6}
          style={{ minHeight: 100, marginTop: 10 }}
        />

        <div className="form-actions">
          <label>
            Premium
            <input
              type="checkbox"
              checked={editMovel ? editMovel.premium : newMovel.premium}
              onChange={(e) => {
                const value = e.target.checked; // Use `checked` to get the boolean value
                editMovel
                  ? setEditMovel({ ...editMovel, premium: value })
                  : setNewMovel({ ...newMovel, premium: value });
              }}
            />
          </label>

          <label>
            Oportunidade
            <input
              type="checkbox"
              checked={editMovel ? editMovel.opportunity : newMovel.opportunity}
              onChange={(e) => {
                const value = e.target.checked; // Use `checked` to get the boolean value
                editMovel
                  ? setEditMovel({ ...editMovel, opportunity: value })
                  : setNewMovel({ ...newMovel, opportunity: value });
              }}
            />
          </label>
        </div>

        {userRole === "Admin" && (
          <select
            value={editMovel ? editMovel.user_id : newMovel.user_id}
            onChange={e => {
              const value = e.target.value;
              editMovel
                ? setEditMovel({ ...editMovel, user_id: value })
                : setNewMovel({ ...newMovel, user_id: value });
            }}
          >
            <option value="">Selecionar Proprietário</option>
            {allUsers.map(user => (
              <option key={user.id} value={user.id}>
                {user.name || user.id}
              </option>
            ))}
          </select>
        )}

        <button onClick={editMovel ? handleUpdateMovel : handleCreateMovel}>
          {editMovel ? "Editar Outro" : "Criar Outro"}
        </button>
      </Modal>

      {loading ? (
        <div>Carregar...</div>
      ) : (
        <table className="moveis-table">
          <thead>
            <tr>
              <th>Estado</th>
              <th>Título</th>
              <th>Distrito</th>
              <th>Cidade</th>
              <th>Premium</th>
              <th>Oportunidade</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {moveis.map((movel) => (
              <tr key={movel.id}>
                <td>
                  <button
                    className={`status-button ${movel.active ? "active" : "inactive"}`}
                    onClick={() => handleToggleActive(movel)}
                  >
                    {movel.active ? "Ativo" : "Inativo"}
                  </button>
                </td>
                <td>{movel.title}</td>
                <td>{movel.district}</td>
                <td>{movel.city}</td>
                <td>{movel.premium ? "Sim" : "Não"}</td>
                <td>{movel.opportunity ? "Sim" : "Não"}</td>
                <td>
                  <button
                    onClick={() => handleEditMovel(movel)}
                  >
                    Editar
                  </button>
                  <button onClick={() => handleDeleteMovel(movel.id, movel.user_id)}>
                    Apagar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MoveisManagementPage;